<template>
  <section class="height-100-p" id="shipment-search-summary-section">
    <el-table
      :data="shipment.list"
      v-loading="shipment.loadingSearch"
      id="shipment-data-list"
      size="mini"
      tooltip-effect="dark"
      sortable
      class="dark-blue-table"
      :empty-text="$t('common.notAbleToFindRecords')"
      @sort-change="handleSortChange"
      :default-sort="{prop: 'shipmentNumber', order: 'ascending'}"
      stripe
    >
     <el-table-column
        prop="shipmentNumber"
        :label="$t('customerService.shipment.shipmentNumber')"
        :min-width="9"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" v-if="scope.row.status === statusError" :content="scope.row.errorMessage" placement="top">
            <span> <i class="el-icon-warning red-rejected"></i> </span>
          </el-tooltip>
          <el-tooltip class="item" effect="dark" :content="scope.row.shipmentNumber" placement="top">
            <span> {{ scope.row.shipmentNumber }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="outboundDate"
        :label="$t('customerService.shipment.outboundDate')"
        :min-width="5"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.outboundDate | moment("MM/DD/YYYY") }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="status"
        :label="$t('customerService.shipment.status')"
        :min-width="4"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <span>{{ shipmentStatusLabel(scope.row.status) }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="shipperAccountName"
        :label="$t('customerService.shipment.shipper')"
        :min-width="6"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.shipperAccountName + ' - ' + scope.row.shipperAccountNumber" placement="top">
            <span> {{ scope.row.shipperAccountName }} - {{ scope.row.shipperAccountNumber }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="consigneeAccountName"
        :label="$t('customerService.shipment.consignee')"
        :min-width="6"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.consigneeAccountName + ' - ' + scope.row.consigneeAccountNumber" placement="top">
            <span> {{ scope.row.consigneeAccountName }} - {{ scope.row.consigneeAccountNumber }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="billToAccountName"
        :label="$t('customerService.shipment.billTo')"
        :min-width="6"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.billToAccountName + ' - ' + scope.row.billToAccountNumber" placement="top">
            <span> {{ scope.row.billToAccountName }} - {{ scope.row.billToAccountNumber }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="location"
        :label="$t('customerService.shipment.location')"
        :min-width="5"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.location" placement="top">
            <span>{{ scope.row.location }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="receivingWarehouseCode"
        :label="$t('customerService.shipment.receivingWarehouse')"
        :min-width="5"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.receivingWarehouseCode }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="outboundWarehouseCode"
        :label="$t('customerService.shipment.outboundWarehouse')"
        :min-width="5"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.outboundWarehouseCode }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="numberOfUnits"
        :label="$t('customerService.shipment.unitsNumber')"
        :min-width="4"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.numberOfUnits }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="cubes"
        :label="$t('customerService.shipment.cubes')"
        :min-width="4"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.cubes.toFixed(2) }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="totalAmount"
        :label="$t('customerService.shipment.totalDollars')"
        :min-width="6"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
            <span>{{ scope.row.totalAmount | currency }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="manualChanges"
        :label="$t('customerService.shipment.manualChanges')"
        :min-width="4"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.manualChanges ? statusBoolean.yes.label : statusBoolean.no.label }}</span>
         </template>
      </el-table-column>
      <el-table-column
        :min-width="4"
        :label="$t('common.options')"
        align="center"
        class-name="fixed-width">
        <template slot-scope="scope">
          <shipment-detail-dialog :shipment-id="scope.row.id" :shipment-status="scope.row.status" />
          <shipment-cancel-button :shipment="scope.row" />
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-footer" v-show="shipment.total > 0">
      <span class="el-pagination__total" slot>
        <span class="el-pagination__title" slot>{{ $t('customerService.shipment.totalLabel') }}</span>
           {{ shipment.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="shipment.actualFilters.rows"
        :current-page.sync="shipment.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="shipment.total"
        layout="slot, sizes, prev, pager, next, jumper"
      >
      </el-pagination>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import constants from '@/utils/constants';
import unitDataTransformLabels from '@/plugins/unitDataTransformLabels';
import { SHIPMENT_SEARCH } from '@/store/actions';
import ShipmentDetailDialog from './components/ShipmentDetailDialog';
import ShipmentCancelButton from './components/ShipmentCancelButton';

export default {
  name: 'ShipmentSummarySection',
  mixins: [unitDataTransformLabels],
  components: { ShipmentDetailDialog, ShipmentCancelButton },
  computed: {
    ...mapGetters(['shipment']),
    statusError() {
      return constants.SHIPMENT.STATUS[3].value;
    },
  },
  data() {
    return {
      sizes: constants.TABLES.DEFAULT_LIMIT_SIZES,
      statusBoolean: constants.STATUS_BOOLEAN
    };
  },
  methods: {
    handleSearchChangeLimit(data) {
      this.$store.dispatch(SHIPMENT_SEARCH, { rows: data, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSearchChangePage(data) {
      this.$store.dispatch(SHIPMENT_SEARCH, { page: data });
    },
    handleSortChange({ prop, order }) {
      this.$store.dispatch(SHIPMENT_SEARCH, {
        orderField: prop,
        orderDirection: constants.TABLES.ORDER_DIRECTION[order]
      });
    }
  }
};
</script>
